import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Generation Panel — global state for the live content-generation UX.
 *
 * The panel + floating chip + n8n canvas are mounted once in the /home
 * layout, so the user can navigate freely while a generation is in progress
 * (the engine continues on Cloud Run regardless). This slice holds:
 *
 *  - which content opportunity + workflow ('brief' | 'draft') we're tracking
 *  - whether the slide-in panel / canvas / step drawer are open
 *  - the latest GenerationStatus snapshot per (contentId, workflow)
 *
 * Status updates arrive via a single root-level <GenerationListener /> that
 * polls `/v1/domains/{id}/contents/{cid}/generation_status/{workflow}` with
 * an adaptive interval (1-5s) and dispatches updateStatus().
 *
 * The slice itself is purely UI state — no thunks. Polling logic lives in
 * the listener component to keep this file plain reducers.
 */

// ── Types (mirror the backend GenerationStatusEmitter schema) ──────────

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
export type WorkflowName = 'brief' | 'draft';
export type WorkflowStatus = 'idle' | 'running' | 'completed' | 'failed';

export interface StepLog {
  step_id: string;
  name: string;
  description: string;
  order: number;
  status: StepStatus;
  started_at: string | null;        // ISO datetime
  completed_at: string | null;
  duration_seconds: number | null;
  output_summary: string | null;
}

export interface GenerationStatus {
  workflow: WorkflowName;
  status: WorkflowStatus;
  current_step_id: string | null;
  completed_step_ids: string[];
  progress_percentage: number;
  started_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  steps: StepLog[];
  /** Backend signals whether the document existed; used to short-circuit polling. */
  exists?: boolean;
}

interface PerContentStatus {
  brief?: GenerationStatus;
  draft?: GenerationStatus;
}

interface GenerationPanelState {
  /** The content_id + workflow currently being tracked. Null when no
   *  generation is active for the current user/domain. */
  activeContentId: string | null;
  activeWorkflow: WorkflowName | null;
  /** Domain ID of the active generation — needed to call the API. */
  activeDomainId: string | null;

  /** UI flags. */
  panelOpen: boolean;
  canvasFullscreenOpen: boolean;
  drawerStepId: string | null;

  /** Latest snapshot, keyed by contentId. Persists across panel close so the
   *  user can re-open and see live state. Multiple workflows per content
   *  (brief then draft) coexist here. */
  statusByContent: Record<string, PerContentStatus>;
}

const initialState: GenerationPanelState = {
  activeContentId: null,
  activeWorkflow: null,
  activeDomainId: null,
  panelOpen: false,
  canvasFullscreenOpen: false,
  drawerStepId: null,
  statusByContent: {},
};

// ── Slice ───────────────────────────────────────────────────────────────

const slice = createSlice({
  name: 'generationPanel',
  initialState,
  reducers: {
    /**
     * Begin tracking a new generation. Called the moment the user clicks
     * "Generate Outline" / "Generate Full Draft", AFTER the API has returned
     * the content_id. Opens the panel automatically.
     */
    setActiveGeneration(
      state,
      action: PayloadAction<{
        contentId: string;
        workflow: WorkflowName;
        domainId: string;
        autoOpenPanel?: boolean;
      }>
    ) {
      state.activeContentId = action.payload.contentId;
      state.activeWorkflow = action.payload.workflow;
      state.activeDomainId = action.payload.domainId;
      state.panelOpen = action.payload.autoOpenPanel !== false;
      state.canvasFullscreenOpen = false;
      state.drawerStepId = null;
    },

    /** Stop tracking entirely. Called when user closes the chip explicitly
     *  on a finished workflow, or when changing domain. */
    clearActiveGeneration(state) {
      state.activeContentId = null;
      state.activeWorkflow = null;
      state.activeDomainId = null;
      state.panelOpen = false;
      state.canvasFullscreenOpen = false;
      state.drawerStepId = null;
    },

    openPanel(state) {
      state.panelOpen = true;
    },
    closePanel(state) {
      state.panelOpen = false;
      // Close any nested overlays too
      state.canvasFullscreenOpen = false;
      state.drawerStepId = null;
    },

    openCanvas(state) {
      state.canvasFullscreenOpen = true;
    },
    closeCanvas(state) {
      state.canvasFullscreenOpen = false;
      state.drawerStepId = null;
    },

    openStepDrawer(state, action: PayloadAction<string>) {
      state.drawerStepId = action.payload;
    },
    closeStepDrawer(state) {
      state.drawerStepId = null;
    },

    /**
     * Update the snapshot for a given (contentId, workflow). Called by the
     * listener after each successful poll. Idempotent — overwrites the prior
     * snapshot since the server doc IS the source of truth.
     */
    updateStatus(
      state,
      action: PayloadAction<{
        contentId: string;
        workflow: WorkflowName;
        status: GenerationStatus;
      }>
    ) {
      const { contentId, workflow, status } = action.payload;
      if (!state.statusByContent[contentId]) {
        state.statusByContent[contentId] = {};
      }
      state.statusByContent[contentId][workflow] = status;
    },
  },
});

export const {
  setActiveGeneration,
  clearActiveGeneration,
  openPanel,
  closePanel,
  openCanvas,
  closeCanvas,
  openStepDrawer,
  closeStepDrawer,
  updateStatus,
} = slice.actions;

export default slice.reducer;

// ── Selectors ───────────────────────────────────────────────────────────

import type { RootState } from './index';

export const selectActiveGeneration = (s: RootState) => ({
  contentId: s.generationPanel.activeContentId,
  workflow: s.generationPanel.activeWorkflow,
  domainId: s.generationPanel.activeDomainId,
});

export const selectActiveStatus = (s: RootState): GenerationStatus | undefined => {
  const cid = s.generationPanel.activeContentId;
  const wf = s.generationPanel.activeWorkflow;
  if (!cid || !wf) return undefined;
  return s.generationPanel.statusByContent[cid]?.[wf];
};

export const selectStatusFor = (
  contentId: string | null | undefined,
  workflow: WorkflowName | null | undefined
) =>
  (s: RootState): GenerationStatus | undefined => {
    if (!contentId || !workflow) return undefined;
    return s.generationPanel.statusByContent[contentId]?.[workflow];
  };

export const selectPanelFlags = (s: RootState) => ({
  panelOpen: s.generationPanel.panelOpen,
  canvasFullscreenOpen: s.generationPanel.canvasFullscreenOpen,
  drawerStepId: s.generationPanel.drawerStepId,
});

/** True iff there is an active workflow that has not yet completed/failed.
 *  Used by FloatingChip to decide whether to render. */
export const selectIsGenerationActive = (s: RootState): boolean => {
  const status = selectActiveStatus(s);
  if (!status) {
    // We're tracking a generation but the snapshot hasn't arrived yet (1st poll
    // pending). Show the chip optimistically as long as there IS an active id.
    return Boolean(s.generationPanel.activeContentId);
  }
  return status.status === 'running' || status.status === 'idle';
};
